import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { GraduationCap, Plus, Loader2, Clock, Building2 } from 'lucide-react';

export default function Training() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assign, setAssign] = useState(null); // course being assigned
  const canManage = ['developer', 'company_admin', 'der', 'owner_operator'].includes(user?.role);
  const load = () => {
    client.get('/training').then((r) => setItems(r.data)).catch(() => setItems([]));
    client.get('/training/catalog').then((r) => setCatalog(r.data)).catch(() => {});
    if (canManage) client.get('/drivers?status=active').then((r) => setDrivers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const complete = async (id) => { try { await client.post(`/training/${id}/complete`); toast.success('Marked complete'); load(); } catch { toast.error('Failed'); } };
  return (
    <AppShell title="Training">
      {canManage && (
        <div className="mb-6">
          <div className="mb-3 font-display text-lg font-semibold">Course Catalog</div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {catalog.map((c) => (
              <Card key={c.id} className="flex flex-col justify-between p-4" data-testid={`course-${c.id}`}>
                <div><div className="mb-1 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-[hsl(var(--gold))]" /><span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">{c.category}</span></div>
                  <div className="font-medium">{c.title}</div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground"><span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{c.provider}</span><span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.duration}</span></div>
                </div>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setAssign(c)} data-testid={`assign-${c.id}`}><Plus className="mr-1.5 h-4 w-4" />Assign</Button>
              </Card>
            ))}
          </div>
        </div>
      )}
      <div className="mb-3 font-display text-lg font-semibold">Assigned Training</div>
      <Card className="overflow-hidden">
        {items === null ? <div className="space-y-2 p-4">{[1,2,3].map(i=><Skeleton key={i} className="h-12" />)}</div> : items.length === 0 ? <div className="p-12 text-center text-sm text-muted-foreground">No training assigned.</div> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr className="border-b text-left text-xs uppercase text-muted-foreground"><th className="p-3">Title</th><th className="p-3">Driver</th><th className="p-3">Provider</th><th className="p-3">Due</th><th className="p-3">Status</th><th className="p-3 text-right">Action</th></tr></thead>
            <tbody>{items.map((it) => (
              <tr key={it.id} className="border-b last:border-0"><td className="p-3 font-medium">{it.title}</td><td className="p-3">{it.driver_name || '-'}</td><td className="p-3 text-sm">{it.provider || 'Internal'}</td><td className="p-3 text-sm">{it.due_date ? new Date(it.due_date).toLocaleDateString() : '-'}</td><td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${it.status==='completed'?'bg-[hsl(var(--success))] text-white':it.status==='overdue'?'bg-[hsl(var(--danger))] text-white':'bg-muted'}`}>{it.status}</span></td><td className="p-3 text-right">{it.status !== 'completed' && <Button size="sm" variant="outline" onClick={() => complete(it.id)} data-testid={`training-complete-${it.id}`}>Complete</Button>}</td></tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <AssignDialog course={assign} onClose={() => setAssign(null)} drivers={drivers} onAssigned={load} />
    </AppShell>
  );
}

function AssignDialog({ course, onClose, drivers, onAssigned }) {
  const [driver, setDriver] = useState('');
  const [due, setDue] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => { setDriver(''); setDue(''); }, [course]);
  const submit = async () => {
    if (!driver) { toast.error('Select a driver'); return; }
    setLoading(true);
    try { await client.post('/training/assign', { course_id: course.id, driver_id: driver, due_date: due ? new Date(due).toISOString() : null }); toast.success('Training assigned'); onAssigned(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };
  return (
    <Dialog open={!!course} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-training-dialog"><DialogHeader><DialogTitle>Assign: {course?.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Driver</Label><Select value={driver} onValueChange={setDriver}><SelectTrigger className="mt-1" data-testid="assign-driver-select"><SelectValue placeholder="Select driver..." /></SelectTrigger><SelectContent>{drivers.map((d) => <SelectItem key={d.driver_id} value={d.driver_id}>{d.full_name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Due Date</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={loading} data-testid="assign-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Assign</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
